import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { HasPermissionsService } from '../../shared/services/has-permissions/has-permissions.service';
import {
  PO_ROLE_TO_PERMISSION,
  PoAction,
} from '../components/purchase-order-detail/purchase-order.constants';

interface PermissionsConfig {
  permissions: Record<string, Record<string, Record<string, string[]>>>;
  common_permissions?: Record<string, { hidden?: string[] }>;
}

/**
 * Loads `permissions.config.json` and provides config-driven action visibility checks.
 *
 * `canPerformAction(sourceType, stage, action)` returns `true` if the config allows
 * the action for that source_type + stage AND the current user has at least one of
 * the permitted roles (checked via HasPermissionsService).
 */
@Injectable({ providedIn: 'root' })
export class PoPermissionsConfigService {
  private readonly CONFIG_URL = 'assets/financial-hub/permissions.config.json';
  private readonly _config$ = new BehaviorSubject<PermissionsConfig | null>(null);

  constructor(
    private readonly http: HttpClient,
    private readonly permissionsService: HasPermissionsService,
  ) {}

  loadConfig(): Observable<PermissionsConfig> {
    return this.http.get<PermissionsConfig>(this.CONFIG_URL).pipe(
      tap(config => this._config$.next(config)),
    );
  }

  get config$(): BehaviorSubject<PermissionsConfig | null> {
    return this._config$;
  }

  canPerformAction(sourceType: string, stage: string | null, action: PoAction): boolean {
    if (!stage) {
      return false;
    }
    const config = this._config$.getValue();
    if (!config) {
      return false;
    }

    const stageConfig = config.permissions?.[sourceType]?.[stage];
    if (!stageConfig) {
      return false;
    }

    for (const roleKey of Object.keys(stageConfig)) {
      const allowedActions = stageConfig[roleKey];
      if (!allowedActions.includes(action)) {
        continue;
      }

      const permissionSlug = PO_ROLE_TO_PERMISSION[roleKey];
      if (!permissionSlug) {
        continue;
      }

      if (this.permissionsService.checkPermission(permissionSlug)) {
        return true;
      }
    }

    return false;
  }

  isCommonActionHidden(action: string, stage: string | null): boolean {
    if (!stage) {
      return false;
    }
    const config = this._config$.getValue();
    if (!config) {
      return false;
    }

    const hiddenStates = config.common_permissions?.[action]?.hidden;
    if (!hiddenStates) {
      return false;
    }

    return hiddenStates.includes(stage);
  }
}
